/**
 * Priority Selector Component
 * Allows setting conversation priority level
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { AlertCircle, ChevronDown } from 'lucide-react';
import { PRIORITY_LEVELS } from './constants';

export const PrioritySelector = ({ 
  currentPriority = 'NORMAL', 
  onPriorityChange,
  size = 'default',
  showLabel = true
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const current = PRIORITY_LEVELS[currentPriority] || PRIORITY_LEVELS.NORMAL;
  
  const handleSelect = (priority) => {
    onPriorityChange(priority);
    setIsOpen(false);
  };
  
  return (
    <div className="relative">
      <Button
        variant="outline"
        size={size === 'small' ? 'sm' : 'default'}
        onClick={() => setIsOpen(!isOpen)}
        className={`${current.bgColor} ${current.textColor} border-current hover:opacity-80`}
      >
        <AlertCircle className={`h-4 w-4 ${size === 'small' ? 'mr-1' : 'mr-2'}`} />
        {showLabel && <span className={size === 'small' ? 'text-xs' : ''}>{current.label}</span>}
        <ChevronDown className={`h-3 w-3 ${showLabel ? 'ml-1' : 'ml-0'}`} />
      </Button>
      
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg border z-50 min-w-[140px]">
            <div className="p-1">
              <p className="text-xs font-medium text-gray-500 px-2 py-1">Priorité</p>
              {Object.values(PRIORITY_LEVELS).map((priority) => (
                <button
                  key={priority.value}
                  onClick={() => handleSelect(priority.value)}
                  className={`
                    w-full flex items-center gap-2 px-2 py-2 text-sm rounded transition-colors
                    ${currentPriority === priority.value 
                      ? `${priority.bgColor} ${priority.textColor}` 
                      : 'hover:bg-gray-100 text-gray-700'
                    }
                  `}
                >
                  <span>{priority.icon}</span>
                  <span>{priority.label}</span>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Badge version for display only
export const PriorityBadge = ({ priority = 'NORMAL', size = 'default' }) => {
  const config = PRIORITY_LEVELS[priority] || PRIORITY_LEVELS.NORMAL;
  
  return (
    <Badge 
      className={`${config.bgColor} ${config.textColor} border-0 ${size === 'small' ? 'text-xs px-1.5 py-0' : ''}`}
    >
      {config.icon} {config.label}
    </Badge>
  );
};

export default PrioritySelector;
